import React, { useState } from "react";
import { 
  Box, Button, Typography, Snackbar, Alert, Select, MenuItem, FormControl, 
  Paper, Stack, Tooltip, InputLabel 
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import api, { API_BASE_URL } from "../../utils/api";

const Uploads = () => {
  const [uploadType, setUploadType] = useState(""); // Stores selected upload type
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(false);

  // ✅ Sample file URLs (Update with actual URLs)
  const sampleFileUrls = {
    ccrviolations: "http://localhost:3200/templates/sample_ccr_violations.xlsx",
    properties: "http://localhost:3200/templates/sample_properties.xlsx",
    workordertypes: "http://localhost:3200/templates/sample_workorder_types.xlsx",
    architecturalrequesttypes: "http://localhost:3200/templates/sample_architectural_request_types.xlsx",
    statuses: "http://localhost:3200/templates/statuses_sample.xlsx" // ✅ NEW
  };

  // ✅ Handle upload type selection
  const handleUploadTypeChange = (event) => {
    setUploadType(event.target.value);
    setSelectedFile(null); // Reset file selection when changing type
  };

  // ✅ Handle file selection
  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  // ✅ Handle file upload
  const handleUpload = async () => {
    if (!selectedFile || !uploadType) {
      setUploadError(true);
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("uploadType", uploadType); // Send upload type

    // ✅ Determine API endpoint based on selection
    const apiEndpoints = {
      ccrviolations: `${API_BASE_URL}/upload-ccr-violations`,
      properties: `${API_BASE_URL}/upload-properties`,
      workordertypes: `${API_BASE_URL}/upload-workorder-types`, 
      architecturalrequesttypes: `${API_BASE_URL}/upload-architectural-request-types`,
      statuses: `${API_BASE_URL}/upload-statuses` // ✅ NEW
    };

    const apiUrl = apiEndpoints[uploadType];

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setUploadSuccess(true);
      } else {
        const errorData = await response.json();
        setUploadError(true);
      }
    } catch (error) {
      console.error("❌ Upload failed:", error);
      setUploadError(true);
    }
  };

  return (
    <Paper elevation={3} sx={{ maxWidth: 600, p: 4, mx: "auto", mt: 5, textAlign: "center", borderRadius: 3 }}>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Upload Files
      </Typography>

      {/* ✅ Step 1: Select Upload Type */}
      <FormControl fullWidth sx={{ mb: 3 }}>
     
        <Select
          value={uploadType}
          onChange={handleUploadTypeChange}
          displayEmpty
          sx={{ height: 45, backgroundColor: "white" }} // ✅ Background color prevents transparency issues
        >
          <MenuItem value="" disabled>Select Upload Type</MenuItem>
          <MenuItem value="ccrviolations">CCR Violation Types</MenuItem>
          <MenuItem value="properties">Properties</MenuItem>
          <MenuItem value="workordertypes">Work Order Types</MenuItem> 
          <MenuItem value="architecturalrequesttypes">Architectural Request Types</MenuItem>
          <MenuItem value="statuses">Statuses</MenuItem>
        </Select>
      </FormControl>

      {/* ✅ Show Upload Instructions After Selection */}
      {uploadType && (
        <Box sx={{ textAlign: "left", mb: 2, p: 2, border: "1px solid #ccc", borderRadius: 2, backgroundColor: "#f9f9f9" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            Instructions:
          </Typography>
          <Typography variant="body2">
            - The file **must be in Excel format (`.xlsx`)**.<br/>
            - Download the sample file, fill in your data, and upload it.<br/>
          </Typography>

          {/* ✅ Download Sample File Button */}
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<DownloadIcon />} 
            sx={{ mt: 2 }}
            href={sampleFileUrls[uploadType]}
            download
          >
            Download Sample File
          </Button>
        </Box>
      )}

      {/* ✅ Step 2: Show Upload Section After Selection */}
      {uploadType && (
        <Stack spacing={2} alignItems="center">
          <input
            type="file"
            accept=".xlsx" // ✅ Restrict to Excel files
            onChange={handleFileChange}
            style={{ display: "none" }}
            id="file-input"
          />
          <label htmlFor="file-input">
            <Tooltip title="Choose an Excel file (.xlsx) to upload" arrow>
              <Button 
                variant="contained" 
                component="span" 
                startIcon={<CloudUploadIcon />} 
                sx={{ backgroundColor: "#1976D2", "&:hover": { backgroundColor: "#115293" } }}
              >
                Choose File
              </Button>
            </Tooltip>
          </label>

          {selectedFile && (
            <Typography sx={{ mt: 1, fontStyle: "italic", color: "#555" }}>
              Selected File: <strong>{selectedFile.name}</strong>
            </Typography>
          )}

          <Button
            variant="contained"
            color="success"
            onClick={handleUpload}
            disabled={!selectedFile}
            sx={{ width: "100%", maxWidth: 200 }}
          >
            Upload
          </Button>
        </Stack>
      )}

      {/* ✅ Success Snackbar */}
      <Snackbar open={uploadSuccess} autoHideDuration={3000} onClose={() => setUploadSuccess(false)}>
        <Alert severity="success" onClose={() => setUploadSuccess(false)}>File uploaded successfully!</Alert>
      </Snackbar>

      {/* ✅ Error Snackbar */}
      <Snackbar open={uploadError} autoHideDuration={3000} onClose={() => setUploadError(false)}>
        <Alert severity="error" onClose={() => setUploadError(false)}>Failed to upload file.</Alert>
      </Snackbar>
    </Paper>
  );
};

export default Uploads;
